import RegularHeader from '../components/Common/RegularHeader';
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Alert from '../components/Alert';
import Loader from '../components/Loader';
import { useNavigate } from 'react-router-dom';
import useOutletRefreshSignal from '../hooks/useOutletRefreshSignal';
import feather from 'feather-icons';
import BottomNavbar from '../components/Home/BottomNavbar';
import AnalyticsChart from '../components/Charts/AnalyticsChart';
import LineChart from '../components/Charts/LineChart';
import CategoryBarChart from '../components/Charts/CategoryBarChart';
import DayWiseLineChart from '../components/Charts/DayWiseLineChart';
import serverClient from '../api/ServerClient';
import { DeleteToken } from '../auth/Auth';
import { amountDisplay, getUserTimezone } from '../util/helper';
import { useLanguage } from '../context/LanguageContext';
import { getPageMeta } from '../util/seoHelper';
import useRefreshSignal from '../hooks/useRefreshSignal';
import { FiTrendingDown, FiCalendar, FiArrowDownLeft, FiArrowUpRight } from 'react-icons/fi';
import '../styles/StatisticsPage.css';

const dayjs = require('dayjs');
const utc = require('dayjs/plugin/utc');
const timezone = require('dayjs/plugin/timezone');

/**
 * Get current month's income and expense data from full statistics
 */
function getCurrentMonthIncomeExpense(fullIncomeExpenseStatistics) {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const currentMonth = dayjs().tz(getUserTimezone()).format('YYYY-MM');

    return fullIncomeExpenseStatistics.find((item) => item.month === currentMonth);
}

/**
 * Get current month's total income with language support
 */
function getCurrentMonthTotalIncome(fullIncomeExpenseStatistics, language) {
    const currentMonthData = getCurrentMonthIncomeExpense(fullIncomeExpenseStatistics);
    return amountDisplay(currentMonthData?.total_income, language);
}

/**
 * Get current month's total expense with language support
 */
function getCurrentMonthTotalExpense(fullIncomeExpenseStatistics, language) {
    const currentMonthData = getCurrentMonthIncomeExpense(fullIncomeExpenseStatistics);
    if (currentMonthData?.total_expenses === 0) {
        return 0;
    }
    return amountDisplay(-currentMonthData?.total_expenses, language);
}

function Statistics() {
    const navigate = useNavigate();
    const { language, t } = useLanguage();
    const refreshSignal = useOutletRefreshSignal();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statisticsData, setStatisticsData] = useState(null);
    const [renderedCharts, setRenderedCharts] = useState(0);
    const [isMobileViewport, setIsMobileViewport] = useState(window.innerWidth < 768);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const statisticsDataRef = useRef(null);

    useEffect(() => {
        statisticsDataRef.current = statisticsData;
    }, [statisticsData]);

    const fetchStatisticsData = useCallback(
        async ({ showLoader = true } = {}) => {
            const shouldShowLoader = showLoader || !statisticsDataRef.current;
            try {
                if (shouldShowLoader) {
                    setLoading(true);
                }
                dayjs.extend(utc);
                dayjs.extend(timezone);
                const now = dayjs().tz(getUserTimezone()).format('YYYY-MM-01');
                const response = await serverClient.get(`/api/user/statistics?month=${now}`);
                setStatisticsData(response?.data);
            } catch (e) {
                if (e.response?.status === 401) {
                    DeleteToken();
                    setError(t('userNotAuthorized'));
                    navigate('/login');
                } else {
                    setError(t('somethingWentWrong'));
                }
            } finally {
                if (shouldShowLoader) {
                    setLoading(false);
                }
            }
        },
        [t, navigate]
    );

    useEffect(() => {
        fetchStatisticsData();
        feather.replace();
    }, [fetchStatisticsData]);

    useRefreshSignal(refreshSignal, () => {
        fetchStatisticsData({ showLoader: false });
    });

    useEffect(() => {
        const onResize = () => {
            setIsMobileViewport(window.innerWidth < 768);
        };

        onResize();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
        updatePreference();

        mediaQuery.addEventListener('change', updatePreference);
        return () => {
            mediaQuery.removeEventListener('change', updatePreference);
        };
    }, []);

    useEffect(() => {
        if (!statisticsData) {
            setRenderedCharts(0);
            return;
        }

        if (!isMobileViewport) {
            setRenderedCharts(4);
            return;
        }

        setRenderedCharts(1);
        const timers = [
            window.setTimeout(() => setRenderedCharts(2), 120),
            window.setTimeout(() => setRenderedCharts(3), 240),
            window.setTimeout(() => setRenderedCharts(4), 360),
        ];

        return () => {
            timers.forEach(window.clearTimeout);
        };
    }, [statisticsData, isMobileViewport]);

    const performanceMode = useMemo(
        () => ({
            compact: isMobileViewport,
            disableAnimation: isMobileViewport || prefersReducedMotion,
            disableEffects: isMobileViewport || prefersReducedMotion,
        }),
        [isMobileViewport, prefersReducedMotion]
    );

    // Loading state
    if (loading || !statisticsData) {
        return (
            <>
                <RegularHeader headerTitle={t('statistics')} />
                <div className="custom-container p-4">
                    {error && <Alert alert={'danger'}>{error}</Alert>}
                    <Loader loading={loading} />
                    <BottomNavbar />
                </div>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>{getPageMeta('statistics', language).title}</title>
                <meta
                    name="description"
                    content={getPageMeta('statistics', language).description}
                />
                <meta name="keywords" content={getPageMeta('statistics', language).keywords} />
                <link rel="canonical" href={getPageMeta('statistics', language).url} />
                {getPageMeta('statistics', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}

                {/* Open Graph Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={getPageMeta('statistics', language).title} />
                <meta
                    property="og:description"
                    content={getPageMeta('statistics', language).description}
                />
                <meta property="og:url" content={getPageMeta('statistics', language).url} />
                <meta property="og:image" content={getPageMeta('statistics', language).image} />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={getPageMeta('statistics', language).title} />
                <meta
                    name="twitter:description"
                    content={getPageMeta('statistics', language).description}
                />
                <meta name="twitter:image" content={getPageMeta('statistics', language).image} />
            </Helmet>
            <RegularHeader headerTitle={t('statistics')} />
            <div className="statistics-page">
                <section>
                    <div className="custom-container">
                        {error && <Alert alert={'danger'}>{error}</Alert>}
                        <Loader loading={loading} />

                        <div className="stats-hero">
                            <div className="stats-hero-grid">
                                <div className="stats-hero-tile">
                                    <div className="tile-icon">
                                        <FiTrendingDown />
                                    </div>
                                    <div>
                                        <h5>{t('expensePerDay')}</h5>
                                        <h3>
                                            {'৳ '}
                                            {amountDisplay(statisticsData.expensePerDay, language)}
                                        </h3>
                                    </div>
                                </div>
                                {statisticsData.todayExpense >= 0 && (
                                    <div className="stats-hero-tile">
                                        <div className="tile-icon">
                                            <FiCalendar />
                                        </div>
                                        <div>
                                            <h5>{t('todayExpense')}</h5>
                                            <h3>
                                                {'৳ '}
                                                {amountDisplay(
                                                    statisticsData.todayExpense,
                                                    language
                                                )}
                                            </h3>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                <section>
                    <div className="custom-container">
                        <h4 className="stats-section-label">{t('thisMonth')}</h4>
                        <div className="stat-cards">
                            <div className="stat-card income">
                                <div className="stat-icon">
                                    <FiArrowDownLeft />
                                </div>
                                <div className="stat-info">
                                    <h5>{t('income')}</h5>
                                    <h3>
                                        {getCurrentMonthTotalIncome(
                                            statisticsData?.fullIncomeExpenseStatistics,
                                            language
                                        )}
                                    </h3>
                                </div>
                            </div>
                            <div className="stat-card expense">
                                <div className="stat-icon">
                                    <FiArrowUpRight />
                                </div>
                                <div className="stat-info">
                                    <h5>{t('expense')}</h5>
                                    <h3>
                                        {getCurrentMonthTotalExpense(
                                            statisticsData?.fullIncomeExpenseStatistics,
                                            language
                                        )}
                                    </h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {renderedCharts >= 1 && (
                    <DayWiseLineChart
                        expenseData={statisticsData?.expenseData}
                        daysInMonth={statisticsData?.daysInMonth}
                        title={t('totalExpense')}
                        performanceMode={performanceMode}
                    />
                )}

                {renderedCharts >= 2 && (
                    <AnalyticsChart
                        fullIncomeExpenseStatistics={statisticsData?.fullIncomeExpenseStatistics}
                        months={statisticsData?.months}
                        performanceMode={performanceMode}
                    />
                )}

                {renderedCharts >= 3 && (
                    <LineChart
                        monthlyTotalBalance={statisticsData?.monthlyTotalBalance}
                        months={statisticsData?.months}
                        performanceMode={performanceMode}
                    />
                )}

                {renderedCharts >= 4 && (
                    <CategoryBarChart
                        categoryWiseExpense={statisticsData?.categoryWiseExpense}
                        performanceMode={performanceMode}
                    />
                )}

                <BottomNavbar />
            </div>
        </>
    );
}

export default Statistics;
